<template>
  <div class="market-details-container" :class="{ embedded: embedded }">
    <div class="market-details-content">
      <div class="page-header">
        <button
          v-if="!embedded"
          class="back-button"
          @click="goBack"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="header-title">
          <img
            v-if="coin?.image?.small"
            :src="coin.image.small"
            :alt="coin.name"
            class="header-icon"
          />
          <h1 class="page-title">{{ coin?.name || $t('marketDetails.loading') }}</h1>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="!coin" class="loading-state">
        <div class="loader-dots">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
        <p class="loading-text">{{ $t('marketDetails.loadingData') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <p class="error-title">{{ $t('marketDetails.unableToLoad') }}</p>
        <p class="error-message">{{ error }}</p>
        <BaseButton variant="outline" @click="loadData">{{ $t('marketDetails.tryAgain') }}</BaseButton>
      </div>

      <!-- Market Content -->
      <div v-else class="market-content">
        <!-- Price Section -->
        <BaseCard class="price-card">
          <div class="price-row">
            <div class="price-info">
              <span class="price-value">
                {{ formatPrice(coin.market_data?.current_price?.usd) }}
              </span>
              <span
                class="price-change"
                :class="{
                  positive: coin.market_data?.price_change_percentage_24h > 0,
                  negative: coin.market_data?.price_change_percentage_24h < 0
                }"
              >
                {{ formatChange(coin.market_data?.price_change_percentage_24h) }}
              </span>
            </div>
            <div class="price-symbol">
              <span class="symbol-text">{{ coin.symbol?.toUpperCase() || '' }}</span>
            </div>
          </div>
          <p class="price-subtext">{{ $t('marketDetails.last24h') }}</p>
        </BaseCard>

        <!-- Chart Section -->
        <BaseCard class="chart-card" v-if="chartPoints.length">
          <h2 class="section-title">{{ $t('marketDetails.priceChart') }}</h2>
          <div class="chart-container">
            <svg
              ref="chartSvg"
              class="price-chart"
              viewBox="0 0 100 40"
              preserveAspectRatio="xMidYMid meet"
              @pointerdown.prevent="handlePointerDown"
              @pointermove.prevent="handlePointerMove"
              @pointerup="handlePointerUp"
              @pointerleave="handlePointerLeave"
            >
              <polyline
                :points="chartPolyline"
                fill="none"
                stroke="#76dfc6"
                stroke-width="0.8"
                stroke-linejoin="round"
                stroke-linecap="round"
              />
              <line
                v-if="hoverX !== null"
                :x1="hoverX"
                y1="5"
                :x2="hoverX"
                y2="35"
                stroke="#9CA3AF"
                stroke-width="0.3"
                stroke-dasharray="1,2"
              />
              <circle
                v-if="hoverX !== null && hoverY !== null"
                :cx="hoverX"
                :cy="hoverY"
                r="1.2"
                fill="#76dfc6"
                stroke="none"
                stroke-width="0.3"
              />
            </svg>

            <div v-if="hoverPrice && hoverTimeLabel" class="chart-tooltip" :style="{ left: hoverXPercent + '%' }">
              <div class="chart-tooltip-price">{{ hoverPrice }}</div>
              <div class="chart-tooltip-time">{{ hoverTimeLabel }}</div>
            </div>

            <div class="chart-axis">
              <span>{{ $t('marketDetails.hoursAgo') }}</span>
              <span>{{ $t('marketDetails.now') }}</span>
            </div>
          </div>
        </BaseCard>

        <!-- Market Info Section -->
        <BaseCard class="info-card">
          <h2 class="section-title">{{ $t('marketDetails.marketInfo') }}</h2>
          <div class="info-list">
            <div class="info-row">
              <span class="info-label">{{ $t('marketDetails.marketCap') }}</span>
              <span class="info-value">
                {{ formatNumber(coin.market_data?.market_cap?.usd) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('marketDetails.circulatingSupply') }}</span>
              <span class="info-value">
                {{ formatNumber(coin.market_data?.circulating_supply) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('marketDetails.volume24h') }}</span>
              <span class="info-value">
                {{ formatNumber(coin.market_data?.total_volume?.usd) }}
              </span>
            </div>
          </div>
        </BaseCard>

        <!-- Project Info Section -->
        <BaseCard v-if="description" class="description-card">
          <h2 class="section-title">{{ $t('marketDetails.projectInfo') }}</h2>
          <p class="description-text">
            {{ description }}
          </p>
        </BaseCard>
      </div>
    </div>

    <BottomMenu v-if="!embedded" />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import BaseCard from '../components/ui/BaseCard.vue'
import BaseButton from '../components/ui/BaseButton.vue'
import BottomMenu from '../components/ui/BottomMenu.vue'
import { marketApi } from '../services/marketApi.js'

export default {
  name: 'MarketDetailsScreen',
  props: {
    coinId: {
      type: String,
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    BaseCard,
    BaseButton,
    BottomMenu
  },
  setup(props) {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()

    const coin = ref(null)
    const chartPoints = ref([])
    const chartSvg = ref(null)
    const hoverIndex = ref(null)
    const error = ref(null)

    const description = computed(() => {
      const en = coin.value?.description?.en || ''
      return en.replace(/<\/?[^>]+(>|$)/g, '').trim()
    })

    const loadData = async () => {
      const id = props.coinId || route.params.id
      if (!id) {
        error.value = t('marketDetails.marketIdNotProvided')
        return
      }

      error.value = null

      try {
        let found = null

        // Сначала пробуем взять данные из sessionStorage
        try {
          const raw = sessionStorage.getItem('hl_selected_market')
          if (raw) {
            const parsed = JSON.parse(raw)
            if (parsed && parsed.id === id) {
              found = parsed
            }
          }
        } catch (e) {
          console.warn('Unable to read cached market:', e)
        }

        // Если в кеше не нашли — запрашиваем с API
        if (!found) {
          const markets = await marketApi.getMarkets({
            perPage: 250,
            order: 'market_cap_desc',
            sparkline: true
          })

          found = Array.isArray(markets)
            ? markets.find((item) => item.id === id)
            : null
        }

        if (!found) {
          error.value = t('marketDetails.marketNotFound')
          return
        }

        // Приводим данные к формату
        coin.value = {
          id: found.id,
          name: found.name,
          symbol: found.symbol,
          image: {
            small: found.image
          },
          market_data: {
            current_price: {
              usd: found.current_price
            },
            price_change_percentage_24h: found.price_change_percentage_24h,
            market_cap: {
              usd: found.market_cap
            },
            total_volume: {
              usd: found.total_volume
            },
            circulating_supply: found.circulating_supply,
            sparkline_7d: {
              price: found.sparkline_in_7d?.price || []
            }
          },
          description: {
            en: ''
          }
        }

        const sparkline = found.sparkline_in_7d?.price
        if (Array.isArray(sparkline) && sparkline.length) {
          const pointsCount = Math.min(sparkline.length, 48)
          chartPoints.value = sparkline.slice(-pointsCount)
        }

        // Отдельно подтягиваем подробную информацию о проекте
        try {
          const details = await marketApi.getCoinDetails(id)
          if (details?.description?.en) {
            coin.value = {
              ...coin.value,
              description: {
                en: details.description.en
              }
            }
          }
        } catch (detailsError) {
          console.warn('Unable to load detailed coin description:', detailsError)
        }
      } catch (err) {
        console.error('Error loading coin details:', err)
        error.value = t('marketDetails.errorLoading')
      }
    }

    const chartStats = computed(() => {
      if (!chartPoints.value.length) return null

      const values = chartPoints.value
      const min = Math.min(...values)
      const max = Math.max(...values)
      const range = max - min || 1
      const stepX = values.length > 1 ? 100 / (values.length - 1) : 0

      return { min, max, range, stepX }
    })

    const chartPolyline = computed(() => {
      if (!chartStats.value) return ''

      const { min, range, stepX } = chartStats.value

      return chartPoints.value
        .map((value, index) => {
          const x = index * stepX
          const normalized = (value - min) / range
          const y = 40 - normalized * 30 - 5
          return `${x.toFixed(2)},${y.toFixed(2)}`
        })
        .join(' ')
    })

    const hoverX = computed(() => {
      if (!chartStats.value || hoverIndex.value === null || chartPoints.value.length < 2) {
        return null
      }
      return hoverIndex.value * chartStats.value.stepX
    })

    const hoverY = computed(() => {
      if (!chartStats.value || hoverIndex.value === null) return null

      const { min, range } = chartStats.value
      const value = chartPoints.value[hoverIndex.value]
      if (value == null) return null

      const normalized = (value - min) / range
      return 40 - normalized * 30 - 5
    })

    const hoverXPercent = computed(() => {
      if (!chartStats.value || hoverIndex.value === null || chartPoints.value.length < 2) {
        return 0
      }
      const maxPercent = 100
      const percent = (hoverIndex.value / (chartPoints.value.length - 1)) * maxPercent
      return Math.min(Math.max(percent, 0), 100)
    })

    const hoverPrice = computed(() => {
      if (hoverIndex.value === null) return null
      const value = chartPoints.value[hoverIndex.value]
      if (value == null) return null
      return formatPrice(value)
    })

    const hoverTimeLabel = computed(() => {
      if (hoverIndex.value === null || chartPoints.value.length < 2) return ''

      const pointsCount = chartPoints.value.length
      const totalMs = 24 * 60 * 60 * 1000
      const stepMs = totalMs / (pointsCount - 1)

      const indexFromEnd = pointsCount - 1 - hoverIndex.value
      const timestamp = Date.now() - indexFromEnd * stepMs
      const date = new Date(timestamp)

      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${hours}:${minutes}`
    })

    const formatPrice = (value) => {
      if (value == null) return '-'
      if (value >= 1000) {
        return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 2 })
      }
      if (value >= 1) {
        return '$' + value.toFixed(2)
      }
      if (value >= 0.01) {
        return '$' + value.toFixed(4)
      }
      return '$' + value.toFixed(6)
    }

    const formatNumber = (value) => {
      if (value == null) return '-'
      if (value >= 1_000_000_000) {
        return '$' + (value / 1_000_000_000).toFixed(1) + 'B'
      }
      if (value >= 1_000_000) {
        return '$' + (value / 1_000_000).toFixed(1) + 'M'
      }
      if (value >= 1_000) {
        return '$' + (value / 1_000).toFixed(1) + 'K'
      }
      return '$' + value.toLocaleString('en-US')
    }

    const formatChange = (value) => {
      if (value == null) return '-'
      const sign = value > 0 ? '+' : ''
      return `${sign}${value.toFixed(2)}%`
    }

    const updateHoverFromEvent = (event) => {
      if (!chartSvg.value || !chartPoints.value.length) return

      const rect = chartSvg.value.getBoundingClientRect()
      const x = event.clientX - rect.left
      const ratio = Math.min(Math.max(x / rect.width, 0), 1)
      const index = Math.round(ratio * (chartPoints.value.length - 1))

      hoverIndex.value = index
    }

    const handlePointerDown = (event) => {
      updateHoverFromEvent(event)
    }

    const handlePointerMove = (event) => {
      updateHoverFromEvent(event)
    }

    const handlePointerUp = () => {
      // оставляем последнюю позицию
    }

    const handlePointerLeave = () => {
      hoverIndex.value = null
    }

    const goBack = () => {
      router.back()
    }

    onMounted(() => {
      loadData()
    })

    return {
      coin,
      chartPoints,
      chartPolyline,
      description,
      error,
      formatPrice,
      formatNumber,
      formatChange,
      goBack,
      loadData,
      chartSvg,
      hoverX,
      hoverY,
      hoverXPercent,
      hoverPrice,
      hoverTimeLabel,
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      handlePointerLeave
    }
  }
}
</script>

<style scoped>
.market-details-container {
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.market-details-container.embedded {
  min-height: auto;
  padding: 0;
  padding-bottom: 0;
  align-items: stretch;
  background: transparent;
}

.market-details-content {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding-bottom: 100px;
}

.market-details-container.embedded .market-details-content {
  max-width: none;
}

.page-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-button:hover {
  background: rgba(118, 223, 198, 0.1);
}

.header-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
}

.header-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-md);
  padding-top: var(--spacing-xl);
}

.loader-dots {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-accent);
  animation: pulse 1.4s ease-in-out infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.error-title {
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--color-text-primary);
}

.error-message {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.market-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.price-card {
  padding: var(--spacing-md);
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xs);
}

.price-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.price-value {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-text-primary);
}

.price-change {
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.price-change.positive {
  color: #22c55e;
}

.price-change.negative {
  color: #ef4444;
}

.price-symbol {
  display: flex;
  align-items: center;
}

.symbol-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 600;
  text-transform: uppercase;
}

.price-subtext {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin: 0;
}

.chart-card {
  padding: var(--spacing-md);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

.chart-container {
  position: relative;
  width: 100%;
  margin-top: var(--spacing-md);
}

.price-chart {
  width: 100%;
  max-width: 100%;
  height: auto;
  aspect-ratio: 3 / 1;
  display: block;
  touch-action: none;
}

.chart-axis {
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-xs);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.chart-tooltip {
  position: absolute;
  top: 8px;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  border-radius: var(--radius-md);
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  pointer-events: none;
  z-index: 1000;
}

.chart-tooltip-price {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-primary);
}

.chart-tooltip-time {
  font-size: 10px;
  color: var(--color-text-secondary);
}

.info-card,
.description-card {
  padding: var(--spacing-md);
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
}

.info-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 500;
}

.info-value {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: 600;
  text-align: right;
}

.description-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  line-height: 1.6;
  margin: 0;
}

@media (max-width: 480px) {
  .market-details-container {
    padding: var(--spacing-md);
  }

  .price-value {
    font-size: var(--font-size-xl);
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-value {
    text-align: left;
  }
}
</style>
